import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {AirDataPollActionTypes, StartAirDataPoll, StopAirDataPoll, UpsertPolledAirData} from '../actions/air-data-poll.actions';
import {exhaustMap, filter, map, switchMap, takeWhile, tap, withLatestFrom} from 'rxjs/operators';
import {AirDatum, PolledAirDatum, StartAirDataPollRequest} from '../models/air-data';
import {DeviceUuid} from '../models/device';
import {AirDataService} from '../services/air-data.service';
import {timer, forkJoin} from 'rxjs';
import {Store} from '@ngrx/store';
import * as fromAfbApi from '../reducers';
import {SelfOrganizationUser} from '../models/organization-user';
import {TemperatureUnit} from '../../shared';
import {retry} from 'rxjs/internal/operators';

@Injectable()
export class AirDataPollEffects {

  private pollInterval: number = 30;
  private timeoutInterval: number = 30;
  private pollSwitch: boolean = false;

  @Effect()
  startAirDataPoll$ = this.actions$.pipe(
    ofType(AirDataPollActionTypes.StartAirDataPoll),
    map((action: StartAirDataPoll) => action.payload),
    // This is to prevent multiple polls
    filter(() => this.pollSwitch !== true),
    // Turn on the polling switch
    tap(() => {
      this.pollSwitch = true;
    }),
    switchMap((startAirDataPollRequest: StartAirDataPollRequest) => timer(0, this.pollInterval * 1000).pipe(
      // This switch prevents polling
      takeWhile(() => this.pollSwitch),
      exhaustMap(() => {
        const pollObs = startAirDataPollRequest.deviceUuids.map((deviceUuid: DeviceUuid) =>
          this.airDataService.getScore({
            organizationToken: startAirDataPollRequest.organizationToken,
            deviceType: deviceUuid.deviceType,
            deviceId: deviceUuid.deviceId,
            desc: true,
            limit: 1
          }).pipe(
            map((airData: AirDatum[]) => {
              return {deviceUuid, airData};
            }),
            retry(3)
          )
        );
        return forkJoin(pollObs);
      }),
      withLatestFrom(this.state$.select(fromAfbApi.selfOrganizationUser)),
      map(([airDataWithDeviceUuid, selfOrganizationUser]:
             [Array<{ deviceUuid: DeviceUuid, airData: AirDatum[] }>, SelfOrganizationUser]) => airDataWithDeviceUuid.reduce(
        (acc: PolledAirDatum[], airDatumWithDeviceUuid: { deviceUuid: DeviceUuid, airData: AirDatum[] }) => {
          const latestAirDatum = airDatumWithDeviceUuid.airData.length > 0 && airDatumWithDeviceUuid.airData[0];
          const deviceUuid = `${airDatumWithDeviceUuid.deviceUuid.deviceType}_${airDatumWithDeviceUuid.deviceUuid.deviceId}`;

          if (latestAirDatum) {
            const now = new Date();
            const latestAirDatumTime = new Date(latestAirDatum.timestamp);
            const diffSeconds = (now.getTime() - latestAirDatumTime.getTime()) / 1000;

            // Convert to fahrenheit if temperature unit is set to fahrenheit
            if (selfOrganizationUser.tempUnit === TemperatureUnit.Fahrenheit) {
              latestAirDatum.sensors = {
                ...latestAirDatum.sensors,
                temp: this.celsiusToFahrenheit(latestAirDatum.sensors.temp)
              };
            }

            const polledAirDatum = {
              ...latestAirDatum,
              deviceUuid,
              // Is offline if difference of seconds is more than 30 seconds
              isOnline: diffSeconds <= this.timeoutInterval
            };

            return [
              ...acc,
              polledAirDatum
            ];
          } else {
            return [...acc];
          }
        }, [])
      ),
      map((polledAirData: PolledAirDatum[]) => new UpsertPolledAirData(polledAirData))
    ))
  );

  @Effect({dispatch: false})
  stopAirDataPoll$ = this.actions$.pipe(
    ofType(AirDataPollActionTypes.StopAirDataPoll),
    tap((action: StopAirDataPoll) => {
      this.pollSwitch = false;
    })
  );

  constructor(private actions$: Actions,
              private state$: Store<fromAfbApi.State>,
              private airDataService: AirDataService) {
  }

  private celsiusToFahrenheit(celsius: number): number {
    return celsius * 9 / 5 + 32;
  }
}
